/**
 * @fileoverview Locale configuration and utilities for internationalization.
 *
 * This module provides configuration for supported locales, default locale settings,
 * and utility functions for locale validation, resolution, and Accept-Language
 * header parsing.
 *
 * @module lib/i18n/locales
 */

/**
 * Complete list of supported locale codes.
 *
 * @description All locales that the application supports for localization.
 * Currently includes English, Vietnamese, Spanish, French, and Japanese.
 *
 * @constant
 */
export const SUPPORTED_LOCALES = ["en", "vi", "es", "fr", "ja"] as const;

/**
 * Union type of all supported locale codes.
 *
 * @description TypeScript type representing any valid locale from SUPPORTED_LOCALES.
 * Used for type-safe locale handling throughout the application.
 */
export type Locale = (typeof SUPPORTED_LOCALES)[number];

/**
 * Default locale to use when user preference cannot be determined.
 *
 * @constant
 */
export const DEFAULT_LOCALE: Locale = "en";

/**
 * Locales we intentionally keep visible/selectable while serving English copy.
 *
 * @description These locales are part of the picker contract, but the mission
 * only guarantees complete catalogs for EN + VI. Until broader translation
 * coverage exists, they should render with the English catalog instead of
 * partial or missing translations.
 */
export const ENGLISH_FALLBACK_LOCALES = ["es", "fr", "ja"] as const;

/**
 * Cookie name for storing user's locale preference.
 *
 * @description The cookie key used to persist the user's selected locale
 * across browser sessions.
 *
 * @constant
 */
export const LOCALE_COOKIE = "aideco.locale";

/**
 * Human-readable labels for each supported locale.
 *
 * @description Maps locale codes to their display names in the language's
 * native script or English, suitable for locale selector UI components.
 *
 * @constant
 */
export const localeLabels: Record<Locale, string> = {
  en: "English",
  vi: "Tiếng Việt",
  es: "Español",
  fr: "Français",
  ja: "日本語",
};

/**
 * User-facing labels for the locale picker that clarify English fallback behavior.
 *
 * For locales that intentionally serve English copy (es, fr, ja), the label
 * appends " — English" so users understand they will see English content when
 * selecting these options. This prevents the selection from being misleading.
 *
 * @constant
 */
export const localePickerLabels: Record<Locale, string> = {
  en: "English",
  vi: "Tiếng Việt",
  es: "Español — English",
  fr: "Français — English",
  ja: "日本語 — English",
};

const rootShellSkipLinkLabels: Record<Locale, string> = {
  en: "Skip to main content",
  vi: "Bỏ qua đến nội dung chính",
  es: "Skip to main content",
  fr: "Skip to main content",
  ja: "Skip to main content",
};

export function getRootShellSkipLinkLabel(locale: Locale): string {
  return rootShellSkipLinkLabels[locale] ?? rootShellSkipLinkLabels[DEFAULT_LOCALE];
}

export function getMessageCatalogLocale(locale: Locale): Locale {
  return ENGLISH_FALLBACK_LOCALES.includes(
    locale as (typeof ENGLISH_FALLBACK_LOCALES)[number],
  )
    ? DEFAULT_LOCALE
    : locale;
}

/**
 * Type guard to check if a value is a valid locale.
 *
 * @description Validates that a value is a non-empty string and exists in the
 * SUPPORTED_LOCALES list. Useful for type narrowing and validation.
 *
 * @param value - The value to check (may be null or undefined)
 * @returns Type predicate - true if value is a valid Locale
 *
 * @example
 * ```typescript
 * import { isLocale } from "@/lib/i18n/locales";
 *
 * if (isLocale(userInput)) {
 *   // TypeScript now knows userInput is of type Locale
 *   console.log(localeLabels[userInput]);
 * }
 * ```
 */
export function isLocale(value: string | null | undefined): value is Locale {
  return !!value && SUPPORTED_LOCALES.includes(value as Locale);
}

/**
 * Resolves a value to a valid locale with fallback to default.
 *
 * @description Validates a locale value and returns it if valid. If the value
 * is invalid (null, undefined, or not in SUPPORTED_LOCALES), returns
 * DEFAULT_LOCALE instead.
 *
 * This function is useful for safely handling user input, query parameters,
 * or cookies that may contain invalid locale values.
 *
 * @param value - The locale value to resolve (may be null or undefined)
 * @returns The validated locale, or DEFAULT_LOCALE if invalid
 *
 * @example
 * ```typescript
 * import { resolveLocale } from "@/lib/i18n/locales";
 *
 * resolveLocale("en");  // "en"
 * resolveLocale("vi");  // "vi"
 * resolveLocale("xx");  // "en" (fallback)
 * resolveLocale(null);  // "en" (fallback)
 * ```
 */
export function resolveLocale(value: string | null | undefined): Locale {
  if (isLocale(value)) {
    return value;
  }
  return DEFAULT_LOCALE;
}

/**
 * Parses an Accept-Language header and returns the best matching locale.
 *
 * @description Parses the HTTP Accept-Language header format (e.g., "en-US,en;q=0.9,vi;q=0.8")
 * and finds the first locale that matches our supported locales. Handles:
 *
 * - Multiple locales separated by commas
 * - Quality values (q=0.9) - currently ignored, processes in order
 * - Language subtags (en-US → en, vi-VN → vi)
 * - Case insensitivity
 *
 * The algorithm:
 * 1. Split header by commas to get individual locale entries
 * 2. Extract the locale code (before semicolon) and normalize to lowercase
 * 3. For each entry, try exact match first
 * 4. If no exact match, try matching the primary language tag (before hyphen)
 * 5. Return the first match found, or DEFAULT_LOCALE if no match
 *
 * @param header - The Accept-Language header string from HTTP request
 * @returns The best matching supported locale, or DEFAULT_LOCALE
 *
 * @example Parse simple header
 * ```typescript
 * import { parseAcceptLanguage } from "@/lib/i18n/locales";
 *
 * parseAcceptLanguage("en-US,en;q=0.9,vi;q=0.8");
 * // Returns: "en"
 * ```
 *
 * @example Parse Vietnamese header
 * ```typescript
 * parseAcceptLanguage("vi-VN,vi;q=0.9,en;q=0.8");
 * // Returns: "vi"
 * ```
 *
 * @example Parse header with unsupported locale
 * ```typescript
 * parseAcceptLanguage("de-DE,de;q=0.9");
 * // Returns: "en" (fallback to default)
 * ```
 *
 * @example Parse header with language variant
 * ```typescript
 * parseAcceptLanguage("en-GB,en-US;q=0.8");
 * // Returns: "en" (matches base language tag)
 * ```
 */
export function parseAcceptLanguage(header: string | null | undefined): Locale {
  if (!header) {
    return DEFAULT_LOCALE;
  }

  const locales = header
    .split(",")
    .map((part) => part.trim().split(";")[0].toLowerCase())
    .filter(Boolean);

  for (const candidate of locales) {
    const normalized = candidate.split("-")[0];
    if (isLocale(candidate as Locale)) {
      return candidate as Locale;
    }
    if (isLocale(normalized as Locale)) {
      return normalized as Locale;
    }
  }

  return DEFAULT_LOCALE;
}
